import { dayjs } from "@thalamos/common";
/**
 * Formats a raw form field value into a human-readable display string.
 * Only handles fields that have a top-level `field` property (not dropdown/multi-select).
 */
function formatFieldValue(field, data) {
    if (!("field" in field))
        return null;
    const fieldKey = field.field;
    const value = data[fieldKey];
    if (value === null || value === undefined || value === "")
        return null;
    switch (field.type) {
        case "textbox":
            return typeof value === "string" ? truncateValue(value) : null;
        case "date":
            return typeof value === "string" ? formatDateValue(value) : null;
        case "date-time":
            return typeof value === "string" ? formatDateTimeValue(value) : null;
        case "time":
            return typeof value === "string" ? value : null;
        case "branch": {
            if (typeof value === "boolean") {
                return value ? "Yes" : "No";
            }
            // Look up the label from the branch options
            const branchField = field;
            const matchedBranch = branchField.branches.find((b) => b.fieldValue === value);
            return matchedBranch
                ? truncateValue(matchedBranch.label)
                : typeof value === "string"
                    ? truncateValue(value)
                    : null;
        }
        case "confirmation-checkbox":
            return value === true ? "Confirmed" : "Not confirmed";
        case "hospital-picker":
        case "place-of-safety-picker": {
            const obj = value;
            const name = obj?.name;
            if (typeof name === "string" && name) {
                const address = obj?.address;
                return typeof address === "string" && address
                    ? `${name}, ${truncateValue(address)}`
                    : name;
            }
            return null;
        }
        case "name-address":
            // Handled as a special case in processFields — should not reach here
            return null;
        case "practitioner": {
            const obj = value;
            const name = obj?.name;
            if (typeof name === "string" && name) {
                return name;
            }
            return null;
        }
        default:
            return typeof value === "string" ? truncateValue(value) : null;
    }
}
function truncateValue(value, maxLength = 120) {
    if (value.length <= maxLength)
        return value;
    return value.substring(0, maxLength) + "…";
}
function formatDateValue(isoString) {
    const d = dayjs.utc(isoString);
    if (!d.isValid())
        return isoString;
    return d.format("DD/MM/YYYY");
}
function formatDateTimeValue(isoString) {
    const d = dayjs.utc(isoString);
    if (!d.isValid())
        return isoString;
    return d.format("DD/MM/YYYY HH:mm");
}
/** Field types that should be excluded from the selectable list */
const EXCLUDED_FIELD_TYPES = new Set([
    "label",
    "separator",
    "readonly",
    "section-12-confirmation-checkbox",
    "approved-clinician-confirmation-checkbox",
    "amph-local-authority-picker",
    "local-authority-picker",
]);
/** Data keys that are auto-populated context fields, not user-editable via the form itself */
const CONTEXT_FIELD_KEYS = new Set(["user"]);
/**
 * Extracts selectable fields from a form part template, paired with their
 * actual data values for display.
 *
 * Returns fields that the MHA admin can select as having errors.
 * Excludes read-only, decorative, and auto-populated context fields.
 */
export function getDeMinimisSelectableFields(partTemplate, data) {
    const results = [];
    function processFields(fields) {
        for (const field of fields) {
            // Include readonly patient info fields (e.g. "Name and address of patient")
            // Split into separate name and address entries when address data exists
            if (field.type === "readonly" &&
                "isReadOnlyPatientInfo" in field &&
                field.isReadOnlyPatientInfo) {
                const patient = data.patient;
                if (patient) {
                    const name = patient.name;
                    if (typeof name === "string" && name) {
                        results.push({
                            fieldKey: "patient.name",
                            label: "Patient name",
                            displayValue: truncateValue(name),
                        });
                    }
                    const addressParts = [
                        typeof patient.address === "string" ? patient.address : "",
                        typeof patient.postalCode === "string" ? patient.postalCode : "",
                    ].filter(Boolean);
                    if (addressParts.length > 0) {
                        results.push({
                            fieldKey: "patient.address",
                            label: "Patient address",
                            displayValue: truncateValue(addressParts.join(", ")),
                        });
                    }
                }
                continue;
            }
            if (EXCLUDED_FIELD_TYPES.has(field.type))
                continue;
            // Multi-select is special — each option is a separate boolean field in the
            // data model, but for de minimis purposes we treat the entire group as one
            // selectable item.  We use a prefixed key ("multiselect::{label}") so the
            // stored fieldKey is clearly a group identifier rather than a single boolean
            // field name.  resolveDeMinimisFieldLabels will match this key back to the
            // same entry because getDeMinimisSelectableFields produces it deterministically.
            if (field.type === "multi-select") {
                const msField = field;
                const selectedLabels = msField.options
                    .filter((opt) => data[opt.field] === true)
                    .map((opt) => opt.label);
                if (msField.label && selectedLabels.length > 0) {
                    results.push({
                        fieldKey: `multiselect::${msField.label}`,
                        label: msField.label,
                        displayValue: truncateValue(selectedLabels.join(", ")),
                    });
                }
                continue;
            }
            // name-address fields produce two selectable entries: name and address
            if (field.type === "name-address") {
                const naField = field;
                const fieldKey = naField.field;
                if (!CONTEXT_FIELD_KEYS.has(fieldKey)) {
                    const obj = data[fieldKey];
                    if (obj) {
                        const name = obj.name;
                        if (typeof name === "string" && name && naField.nameLabel) {
                            results.push({
                                fieldKey: `${fieldKey}.name`,
                                label: truncateValue(naField.nameLabel, 200),
                                displayValue: truncateValue(name),
                            });
                        }
                        const addressParts = [
                            typeof obj.address === "string" ? obj.address : "",
                            typeof obj.postalCode === "string" ? obj.postalCode : "",
                        ].filter(Boolean);
                        if (addressParts.length > 0 && naField.addressLabel) {
                            results.push({
                                fieldKey: `${fieldKey}.address`,
                                label: truncateValue(naField.addressLabel, 200),
                                displayValue: truncateValue(addressParts.join(", ")),
                            });
                        }
                    }
                }
                continue;
            }
            // For dropdown fields, use valueField as key (dropdown has no 'field' prop)
            if (field.type === "dropdown") {
                const dropdownField = field;
                const textValue = data[dropdownField.textField];
                if (typeof textValue === "string" && textValue) {
                    results.push({
                        fieldKey: dropdownField.valueField,
                        label: truncateValue(dropdownField.label, 200),
                        displayValue: textValue,
                    });
                }
            }
            else if ("field" in field) {
                // For fields with a 'field' property
                const fieldKey = field.field;
                if (!CONTEXT_FIELD_KEYS.has(fieldKey)) {
                    // Most fields use 'label', but name-address uses 'nameLabel'/'addressLabel'
                    const label = "label" in field
                        ? field.label
                        : "nameLabel" in field
                            ? field.nameLabel
                            : undefined;
                    if (label) {
                        const displayValue = formatFieldValue(field, data);
                        if (displayValue) {
                            results.push({
                                fieldKey,
                                label: truncateValue(label, 200),
                                displayValue,
                            });
                        }
                    }
                }
            }
            // Recurse into branch sub-fields for the selected branch
            if (field.type === "branch") {
                const branchField = field;
                const currentValue = data[branchField.field];
                const activeBranch = branchField.branches.find((b) => b.fieldValue === currentValue);
                if (activeBranch) {
                    processFields(activeBranch.fields);
                }
            }
        }
    }
    processFields(partTemplate.formBuilderFields);
    return results;
}
/**
 * Resolves stored field keys back to their labels using the form part template and data.
 * Used by the response modal to display which fields were flagged.
 */
export function resolveDeMinimisFieldLabels(partTemplate, data, selectedFieldKeys) {
    const allFields = getDeMinimisSelectableFields(partTemplate, data);
    const keySet = new Set(selectedFieldKeys);
    return allFields.filter((f) => keySet.has(f.fieldKey));
}
//# sourceMappingURL=deMinimisFieldHelpers.js.map